import { useEffect, useState } from 'react';

/**
 * OverlapHintTooltip — brief, self-dismissing hint shown near a plant
 * when its overlap state changes to 'warning' or 'critical'.
 *
 * Appears for ~3 s, then fades out over 0.5 s.
 * Re-triggers if the state changes again while visible.
 */

const MESSAGES = {
  warning: 'Wuchsbereich überschneidet sich',
  critical: 'Pflanzabstand unterschritten',
};

const COLORS = {
  warning: { bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-800', dot: 'bg-amber-400' },
  critical: { bg: 'bg-red-50', border: 'border-red-400', text: 'text-red-800', dot: 'bg-red-400' },
};

const OverlapHintTooltip = ({ state, position, visible }) => {
  const [show, setShow] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    // When not visible the render guard below already hides the tooltip, so the
    // effect only needs to (re-)trigger the timed reveal — no synchronous reset.
    if (!visible || !state || state === 'none') {
      return;
    }

    // (Re-)trigger on every relevant state change. The reveal is flipped on the
    // next frame (not synchronously in the effect body) to avoid a
    // setState-in-effect cascade; the ~1-frame delay is imperceptible.
    const raf = requestAnimationFrame(() => {
      setShow(true);
      setFading(false);
    });
    const fadeTimer = setTimeout(() => setFading(true), 2500);
    const hideTimer = setTimeout(() => {
      setShow(false);
      setFading(false);
    }, 3000);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [visible, state]);

  if (!show || !position || !state || state === 'none') return null;

  const c = COLORS[state] || COLORS.warning;
  const message = MESSAGES[state] || '';

  return (
    <div
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, 12px)',
        zIndex: 9999,
        pointerEvents: 'none',
        opacity: fading ? 0 : 1,
        transition: 'opacity 0.5s ease-out',
      }}
    >
      <div
        className={`${c.bg} ${c.border} ${c.text} border text-xs font-medium pl-2 pr-2.5 py-1 rounded-md shadow-sm whitespace-nowrap flex items-center gap-1.5`}
      >
        <span className={`${c.dot} inline-block w-1.5 h-1.5 rounded-full shrink-0`} />
        {message}
      </div>
    </div>
  );
};

export default OverlapHintTooltip;
